import {EditorState, type Extension, RangeSet, RangeSetBuilder, StateField} from '@codemirror/state'
import {BlockWrapper, Decoration, type DecorationSet, EditorView, WidgetType} from '@codemirror/view'
import {clsx} from 'clsx'
import type {Element, Nodes} from 'hast'
import {h} from 'hastscript'
import {SKIP, visitParents} from 'unist-util-visit-parents'

import {getDelimiterType} from '../../language-tools/html/extensions/delimiters'
import {hastPropertiesToHtmlAttributes, isTag} from '../../language-tools/html/hast-utils'
import {isChecked} from '../../language-tools/markdown/list-utils'
import {isPositionedNode} from '../../language-tools/syntax-tree/positioned-node'
import {CharacterReferenceWidget} from '../widgets/character-reference'
import {HastElementWidget} from '../widgets/hast-element'
import {RenderErrorWidget} from '../widgets/render-error'
import {TaskListCheckboxWidget} from '../widgets/task-list-checkbox'
import {VideoWidget} from '../widgets/video'
import {activeNodesChanged, activeNodesExtension, getActiveNodes} from './active-nodes'
import {isKnownInvalidVideoUrl, videoUrlsExtension} from './invalid-video-urls'
import {getHtmlTree, htmlTreeChanged, markdownParserExtension} from './markdown-parser'
import styles from './style-contents.module.css'

/** Ancestor node types with data about whether they are active or not. For handling children of active nodes. */
type NodeContext = Array<{node: Nodes; active: boolean}>

// `root` is always active when the editor is focused since it covers the entire document
const withinActiveBlock = (context: NodeContext, nodeActive: boolean) =>
  nodeActive || context.some(parent => parent.active && parent.node.type === 'element')

/** Check if this element is a link defined with `[]()` syntax. */
const isResourceLink = (element: Element) =>
  element.tagName === 'a' &&
  element.children.some(child => child.type === 'element' && child.properties?.dataDelimiter === 'resource')

/** Tag names to style as blocks. */
const blockElements = new Set([
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'pre',
  'ul',
  'ol',
  'dl',
  'li',
  'dd',
  'dt',
  'p',
  'table',
  'hr',
  'div',
  'details',
  'video',
])

/** Check if this node is a setext heading node with only one character in the delimiter line. */
function isSingleCharSetextHeading(element: Element) {
  return element.children.some(
    child =>
      child.type === 'element' &&
      child.tagName === 'span' &&
      child.properties?.dataDelimiter === 'setextHeadingLine' &&
      child.children?.[0]?.type === 'text' &&
      child.children[0].value.trim().length === 1,
  )
}

function markDecoration(element: Element) {
  // Use `inclusive: true` so the mark includes its start and end positions.
  // This prevents rendering issues when a replace decoration is applied at a node's start or end
  // and would otherwise conflict with the mark.
  return Decoration.mark({
    tagName: element.tagName,
    attributes: hastPropertiesToHtmlAttributes(element.properties),
    inclusive: true,
  })
}

function widgetDecoration(widget: WidgetType) {
  // Our 'block' widgets which get the block class are not the same thing as CodeMirror block widgets which render in
  // between lines -- `block` is always false (the default) for our widgets.
  return Decoration.replace({widget})
}

function elementWidget(element: Element, block: boolean, active: boolean) {
  return widgetDecoration(
    new HastElementWidget(
      block ? h('div', {class: styles.blockWidget}, element) : element,
      block ? blockClass(active) : undefined,
    ),
  )
}

function blockClass(active: boolean) {
  return clsx(styles.block, active && styles.activeBlock)
}

function blockWrapper(element: Element, active: boolean) {
  const {className, ...properties} = element.properties

  return BlockWrapper.create({
    tagName: element.tagName,
    attributes: {
      class: clsx(blockClass(active), className),
      ...hastPropertiesToHtmlAttributes(properties),
    },
  })
}

function getElementDecoration(
  element: Element,
  active: boolean,
  context: NodeContext,
  state: EditorState,
): Decoration | BlockWrapper | undefined {
  if (element.properties.dataBlankLine) return Decoration.line({class: blockClass(active)})

  switch (element.tagName) {
    // In an active block, links render with only the `resourceDestination` delimiter node highlighted
    case 'a':
      if (withinActiveBlock(context, active) && isResourceLink(element)) return undefined
      break

    // Tables revert to plain text when active (for now, until we build a table editor)
    case 'table':
      return active ? blockWrapper(h('div'), active) : elementWidget(element, true, active)

    // Table contents are rendered via widget contents (not decorations) so these never need to apply
    case 'tr':
    case 'th':
    case 'td':
    case 'thead':
    case 'tbody':
      return undefined

    // Details/summary tags are not supported in contenteditable
    case 'details':
      return blockWrapper(h('div'), active)
    case 'summary':
      return markDecoration(h('span', {className: styles.summary}))

    // Void elements must be rendered with widgets since they fully replace their contents rather than wrapping them
    case 'hr':
      return active ? blockWrapper(h('div'), active) : elementWidget(element, true, active)
    case 'video':
      // Known invalid URLs are videos that we tried and failed to render. They probably point to non-video files
      return active ||
        isKnownInvalidVideoUrl(state, typeof element.properties?.src === 'string' ? element.properties.src : '')
        ? blockWrapper(h('div', {className: styles.muted}), active)
        : widgetDecoration(new VideoWidget(element, blockClass(active)))

    case 'img':
    case 'br':
      return withinActiveBlock(context, active) ? undefined : elementWidget(element, false, active)

    // Delimiter special cases
    case 'span': {
      const delimiterType = getDelimiterType(element)
      if (!delimiterType) break

      const raw = element.children[0]?.type === 'text' ? element.children[0].value : ''

      // character reference delimiters have to be replaced with the reference value via a widget
      if (delimiterType === 'characterReference' && !withinActiveBlock(context, active))
        return widgetDecoration(new CharacterReferenceWidget(raw))

      // Links are wierd because we move the anchor to the destination url when active
      if (delimiterType === 'resourceDestination' && withinActiveBlock(context, active))
        return markDecoration(h('a', {href: raw}))

      // Tasklist checkboxes are rendered as clickable/editable checkboxes when not in an active block
      if (delimiterType === 'taskListCheck' && !withinActiveBlock(context, active)) {
        if (!isPositionedNode(element)) break
        const startOffset = element.position.start.offset
        const endOffset = element.position.end.offset

        const checked = isChecked(raw)
        return widgetDecoration(
          new TaskListCheckboxWidget({
            checked,
            from: startOffset,
            to: endOffset,
          }),
        )
      }

      break
    }

    case 'h2':
      // Avoid flicker when typing lists that immediately follow text (https://github.com/github/core-ux/issues/838)
      if (active && isSingleCharSetextHeading(element)) return blockWrapper(h('div'), active)
      break

    case 'code': {
      // Code blocks are pre > code, so <code> is block only if it is a direct child of <pre>
      const parentNode = context.at(-1)?.node
      if (parentNode && isTag(parentNode, 'pre')) return blockWrapper(element, active)
      break
    }
  }

  return blockElements.has(element.tagName) ? blockWrapper(element, active) : markDecoration(element)
}

function getNodeDecoration(
  node: Nodes,
  active: boolean,
  context: NodeContext,
  state: EditorState,
): Decoration | BlockWrapper | undefined {
  switch (node.type) {
    case 'comment':
      return blockWrapper(h('code', {className: styles.comment}), active)
    case 'element':
      return getElementDecoration(node, active, context, state)
  }
}

function buildDecorations(state: EditorState): DecorationsState {
  const tree = getHtmlTree(state)
  const activeNodes = getActiveNodes(state)

  const decorationsBuilder = new RangeSetBuilder<Decoration>()
  const blockWrappersBuilder = new RangeSetBuilder<BlockWrapper>()

  visitParents(tree, (node, parents) => {
    if (!isPositionedNode(node)) return

    const nodeContext: NodeContext = parents.map(p => ({node: p, active: activeNodes.has(p)}))

    const decoration = getNodeDecoration(node, activeNodes.has(node), nodeContext, state)
    if (decoration) {
      // For decorations with the same 'from' and 'to' value, CodeMirror renders the first tag in the set and wraps
      // the second tag around it. However, our tree walker encounters parents first and then children, which causes
      // incorrect element order. For example, a list with one element would result in `<li><ol>...</ol></li>`
      // because the list and item have the same start and end offsets.
      //
      // To fix this, CodeMirror supports a secondary comparison by `startSide`. Lower `startSide` = outermost
      // element.
      decoration.startSide = parents.length

      if (decoration instanceof BlockWrapper) {
        blockWrappersBuilder.add(node.position.start.offset, node.position.end.offset, decoration)
      } else {
        decorationsBuilder.add(node.position.start.offset, node.position.end.offset, decoration)

        // Skip children if a widget was rendered, since the widget will replace all content at this range; any
        // decorations inside this range would be ignored anyway
        if ('widget' in decoration.spec) return SKIP
      }
    }
  })

  return {
    decorations: decorationsBuilder.finish(),
    blockWrappers: blockWrappersBuilder.finish(),
  }
}

let lastReportedError: Error | undefined = undefined

/**
 * Attempt to build decorations, falling back to showing an error widget on failure. Careful error handling here is
 * important because there are many subtle ways this can fail if the code is imperfect (eg, decorations added out of
 * order or with ranges outside the document bounds). The default handling for errors in CodeMirror breaks the editor
 * in unexpected and potentially subtle ways, so this ensures that users know there is an issue and provides a valid
 * fallback.
 */
function tryWithFallback(fn: () => DecorationsState): DecorationsState {
  try {
    const result = fn()
    return result
  } catch (error) {
    if (error instanceof Error) {
      // Errors will repeat on every keystroke if the user keeps typing, which can cause excessive noise in error logs.
      // There's no need to rethrow the same error over and over when nothing has changed.
      if (!lastReportedError || lastReportedError.name !== error.name || lastReportedError.message !== error.message) {
        setTimeout(() => {
          throw error // rethrow async for console logging and error reporting
        })
      }
      lastReportedError = error
    }

    const builder = new RangeSetBuilder<Decoration>()
    builder.add(0, 0, Decoration.widget({widget: new RenderErrorWidget()}))
    return {
      decorations: builder.finish(),
      blockWrappers: RangeSet.empty,
    }
  }
}

interface DecorationsState {
  decorations: DecorationSet
  blockWrappers: RangeSet<BlockWrapper>
}

const decorationsState = StateField.define<DecorationsState>({
  create: state => tryWithFallback(() => buildDecorations(state)),
  update: (prev, tr) => {
    const treeChanged = htmlTreeChanged(tr)

    return tryWithFallback(() => {
      if (tr.docChanged && !treeChanged) {
        // 'mapping' the decorations just updates their positions based on where text was inserted/deleted to keep them
        // relatively in sync position-wise. It prevents text from flowing out of the end of nodes, but doesn't
        // actually reflect any Markdown formatting changes. For that, we wait until the tree parse event which we know
        // will quickly follow a docChanged event.
        return {decorations: prev.decorations.map(tr.changes), blockWrappers: prev.blockWrappers.map(tr.changes)}
      } else if (treeChanged || activeNodesChanged(tr)) {
        return buildDecorations(tr.state)
      } else {
        return prev
      }
    })
  },
  provide: field => [
    markdownParserExtension,
    activeNodesExtension,
    videoUrlsExtension,
    EditorView.outerDecorations.from(field, val => val.decorations),
    EditorView.blockWrappers.from(field, val => val.blockWrappers),
    EditorView.contentAttributes.of({class: clsx('markdown-body', styles.container)}),
  ],
})

export const styleContentsExtension: Extension = decorationsState
